"use strict";

(() => {
	class Retirement_Age_Calculator {
		/** @var {HTMLElement} */
		#$widget;

		/** @var {NodeListOf<HTMLInputElement>} */
		#$sexInputs;

		/** @var {HTMLInputElement} */
		#$birthdateInput;

		/** @var {AirDatepicker} */
		#birthdateDatepicker;

		/** @var {HTMLInputElement} */
		#$childrenInput;

		/** @var {HTMLElement} */
		#$result;

		/** @var {HTMLElement} */
		#$resultContent;

		/** @var {HTMLTemplateElement} */
		#$resultTemplateStandard;

		/** @var {HTMLTemplateElement} */
		#$resultTemplateAfter;


		constructor($widget) {
			this.#$widget = $widget;
		}

		initialize() {
			// Get elements.
			this.#$sexInputs       = this.#$widget.querySelectorAll(".dan-inputs-sex input[type='radio']");
			this.#$birthdateInput  = this.#$widget.querySelector(".dan-input-birthdate");
			this.#$childrenInput   = this.#$widget.querySelector(".dan-input-children");

			this.#$result                 = this.#$widget.querySelector(".dan-result");
			this.#$resultContent          = this.#$widget.querySelector(".dan-result-content");
			this.#$resultTemplateStandard = this.#$widget.querySelector(".dan-template-result-standard");
			this.#$resultTemplateAfter    = this.#$widget.querySelector(".dan-template-result-after");

			// Extend Day.js with Duration plugin.
			window.dayjs.extend(window.dayjs_plugin_duration);

			// Initialize Air Datepicker.
			this.#birthdateDatepicker = new AirDatepicker(this.#$birthdateInput, {
				locale: AirDatepickerLocaleCs,
				selectedDates: (this.#$birthdateInput.value)
					? [this.#$birthdateInput.value]
					: [],
				dateFormat: "dd. MM. yyyy",
				position: "bottom right",
				view: "years",
				keyboardNav: false,
				toggleSelected: false,
				onSelect: () => {
					// noinspection JSUnresolvedReference
					this.#birthdateDatepicker.hide()
				}
			});

			// Initialize children quantity input.
			const $childrenGroup = this.#$widget.querySelector(".dan-children-group");
			$childrenGroup.quantityInput = new Dan_QuantityInput($childrenGroup);
			$childrenGroup.quantityInput.initialize();

			// Attach handlers to events.
			this.#$widget.addEventListener("submit", (this.#onFormSubmit).bind(this));
			this.#$birthdateInput.addEventListener("input", (this.#onBirthdateInput).bind(this));

			for (const $input of this.#$sexInputs) {
				$input.addEventListener("change", (this.#refreshChildrenInputVisibility).bind(this));
			}
		}

		/**
		 * @param {SubmitEvent} event
		 */
		#onFormSubmit(event) {
			event.preventDefault();

			// Load inputs.
			const inputs = this.getInputs();

			if (!inputs.sex || !["man", "woman"].includes(inputs.sex)) {
				alert("Prosím, zvolte pohlaví.");
				return;
			}

			if (!inputs.birthdate) {
				alert("Prosím, zadejte své datum narození.");
				return;
			}

			// Calc and format result.
			const retirementAge = this.#getRetirementAge(
				inputs.sex,
				inputs.birthdate,
				inputs.children
			);

			const retirementDate = window.dayjs(inputs.birthdate)
				.add(retirementAge.years, "years")
				.add(retirementAge.months, "months");

			// Day.js Durations doesn't count with leap years,
			// so we have to subtract 1 day for each leap year manually.
			const todayDate      = window.dayjs();
			let countdownDate    = window.dayjs.duration(retirementDate.diff(todayDate));
			const countdownYears = parseInt(countdownDate.format("Y"));
			const leapYears      = Math.floor(countdownYears / 4);

			if (leapYears > 0) {
				countdownDate = countdownDate.subtract(leapYears, "days");
			}

			const resultType      = (retirementDate > todayDate) ? "standard" : "after";
			const resultAge       = this.#formatRetirementAge(retirementAge);
			const resultDate      = retirementDate.format("D. M. YYYY");
			const resultCountdown = this.#formatCountdownDate(countdownDate);

			if (this.#$result.classList.contains("-active")) {
				this.#slideResultUp(() => {
					this.#setResult(
						resultType,
						resultAge,
						resultDate,
						resultCountdown
					);

					this.#slideResultDown();
				});
			} else {
				this.#setResult(
					resultType,
					resultAge,
					resultDate,
					resultCountdown
				);

				this.#slideResultDown();
			}
		}

		/**
		 * @param {string} sex
		 * @param {Date} birthdate
		 * @param {number} children
		 *
		 * @see https://www.cssz.cz/starobni-duchod-podrobne
		 */
		#getRetirementAge(sex, birthdate, children) {
			const birthdateYear = birthdate.getFullYear();

			// Retirement age for men and women born after 1971 is 65 years.
			if (birthdateYear > 1971) {
				return {
					years: 65,
					months: 0
				}
			}

			// Table for those born between 1936 and 1971.
			const retirementTable = {
				1936: {
					man: [60, 2],
					woman: {
						"0": [57, 0],
						"1": [56, 0],
						"2": [55, 0],
						"3,4": [54, 0],
						"more": [53, 0]
					}
				},
				1937: {
					man: [60, 4],
					woman: {
						"0": [57, 0],
						"1": [56, 0],
						"2": [55, 0],
						"3,4": [54, 0],
						"more": [53, 0]
					}
				},
				1938: {
					man: [60, 6],
					woman: {
						"0": [57, 0],
						"1": [56, 0],
						"2": [55, 0],
						"3,4": [54, 0],
						"more": [53, 0]
					}
				},
				1939: {
					man: [60, 8],
					woman: {
						"0": [57, 4],
						"1": [56, 0],
						"2": [55, 0],
						"3,4": [54, 0],
						"more": [53, 0]
					}
				},
				1940: {
					man: [60, 10],
					woman: {
						"0": [57, 8],
						"1": [56, 4],
						"2": [55, 0],
						"3,4": [54, 0],
						"more": [53, 0]
					}
				},
				1941: {
					man: [61, 0],
					woman: {
						"0": [58, 0],
						"1": [56, 8],
						"2": [55, 4],
						"3,4": [54, 0],
						"more": [53, 0]
					}
				},
				1942: {
					man: [61, 2],
					woman: {
						"0": [58, 4],
						"1": [57, 0],
						"2": [55, 8],
						"3,4": [54, 4],
						"more": [53, 0]
					}
				},
				1943: {
					man: [61, 4],
					woman: {
						"0": [58, 8],
						"1": [57, 4],
						"2": [56, 0],
						"3,4": [54, 8],
						"more": [53, 4]
					}
				},
				1944: {
					man: [61, 6],
					woman: {
						"0": [59, 0],
						"1": [57, 8],
						"2": [56, 4],
						"3,4": [55, 0],
						"more": [53, 8]
					}
				},
				1945: {
					man: [61, 8],
					woman: {
						"0": [59, 4],
						"1": [58, 0],
						"2": [56, 8],
						"3,4": [55, 4],
						"more": [54, 0]
					}
				},
				1946: {
					man: [61, 10],
					woman: {
						"0": [59, 8],
						"1": [58, 4],
						"2": [57, 0],
						"3,4": [55, 8],
						"more": [54, 4]
					}
				},
				1947: {
					man: [62, 0],
					woman: {
						"0": [60, 0],
						"1": [58, 8],
						"2": [57, 4],
						"3,4": [56, 0],
						"more": [54, 8]
					}
				},
				1948: {
					man: [62, 2],
					woman: {
						"0": [60, 4],
						"1": [59, 0],
						"2": [57, 8],
						"3,4": [56, 4],
						"more": [55, 0]
					}
				},
				1949: {
					man: [62, 4],
					woman: {
						"0": [60, 8],
						"1": [59, 4],
						"2": [58, 0],
						"3,4": [56, 8],
						"more": [55, 4]
					}
				},
				1950: {
					man: [62, 6],
					woman: {
						"0": [61, 0],
						"1": [59, 8],
						"2": [58, 4],
						"3,4": [57, 0],
						"more": [55, 8]
					}
				},
				1951: {
					man: [62, 8],
					woman: {
						"0": [61, 4],
						"1": [60, 0],
						"2": [58, 8],
						"3,4": [57, 4],
						"more": [56, 0]
					}
				},
				1952: {
					man: [62, 10],
					woman: {
						"0": [61, 8],
						"1": [60, 4],
						"2": [59, 0],
						"3,4": [57, 8],
						"more": [56, 4]
					}
				},
				1953: {
					man: [63, 0],
					woman: {
						"0": [62, 0],
						"1": [60, 8],
						"2": [59, 4],
						"3,4": [58, 0],
						"more": [56, 8]
					}
				},
				1954: {
					man: [63, 2],
					woman: {
						"0": [62, 4],
						"1": [61, 0],
						"2": [59, 8],
						"3,4": [58, 4],
						"more": [57, 0]
					}
				},
				1955: {
					man: [63, 4],
					woman: {
						"0": [62, 8],
						"1": [61, 4],
						"2": [60, 0],
						"3,4": [58, 8],
						"more": [57, 4]
					}
				},
				1956: {
					man: [63, 6],
					woman: {
						"0": [63, 2],
						"1": [61, 8],
						"2": [60, 4],
						"3,4": [59, 0],
						"more": [57, 8]
					}
				},
				1957: {
					man: [63, 8],
					woman: {
						"0": [63, 8],
						"1": [62, 2],
						"2": [60, 8],
						"3,4": [59, 4],
						"more": [58, 0]
					}
				},
				1958: {
					man: [63, 10],
					woman: {
						"0": [63, 10],
						"1": [62, 8],
						"2": [61, 2],
						"3,4": [59, 8],
						"more": [58, 4]
					}
				},
				1959: {
					man: [64, 0],
					woman: {
						"0": [64, 0],
						"1": [63, 2],
						"2": [61, 8],
						"3,4": [60, 2],
						"more": [58, 8]
					}
				},
				1960: {
					man: [64, 2],
					woman: {
						"0": [64, 2],
						"1": [63, 8],
						"2": [62, 2],
						"3,4": [60, 8],
						"more": [59, 2]
					}
				},
				1961: {
					man: [64, 4],
					woman: {
						"0": [64, 4],
						"1": [64, 2],
						"2": [62, 8],
						"3,4": [61, 2],
						"more": [59, 8]
					}
				},
				1962: {
					man: [64, 6],
					woman: {
						"0": [64, 6],
						"1": [64, 6],
						"2": [63, 2],
						"3,4": [61, 8],
						"more": [60, 2]
					}
				},
				1963: {
					man: [64, 8],
					woman: {
						"0": [64, 8],
						"1": [64, 8],
						"2": [63, 8],
						"3,4": [62, 2],
						"more": [60, 8]
					}
				},
				1964: {
					man: [64, 10],
					woman: {
						"0": [64, 10],
						"1": [64, 10],
						"2": [64, 2],
						"3,4": [62, 8],
						"more": [61, 2]
					}
				},
				1965: {
					man: [65, 0],
					woman: {
						"0": [65, 0],
						"1": [65, 0],
						"2": [64, 8],
						"3,4": [63, 2],
						"more": [61, 8]
					}
				},
				1966: {
					man: [65, 0],
					woman: {
						"0": [65, 0],
						"1": [65, 0],
						"2": [65, 0],
						"3,4": [63, 8],
						"more": [62, 2]
					}
				},
				1967: {
					man: [65, 0],
					woman: {
						"0": [65, 0],
						"1": [65, 0],
						"2": [65, 0],
						"3,4": [64, 2],
						"more": [62, 8]
					}
				},
				1968: {
					man: [65, 0],
					woman: {
						"0": [65, 0],
						"1": [65, 0],
						"2": [65, 0],
						"3,4": [64, 8],
						"more": [63, 2]
					}
				},
				1969: {
					man: [65, 0],
					woman: {
						"0": [65, 0],
						"1": [65, 0],
						"2": [65, 0],
						"3,4": [65, 0],
						"more": [63, 8]
					}
				},
				1970: {
					man: [65, 0],
					woman: {
						"0": [65, 0],
						"1": [65, 0],
						"2": [65, 0],
						"3,4": [65, 0],
						"more": [64, 2]
					}
				},
				1971: {
					man: [65, 0],
					woman: {
						"0": [65, 0],
						"1": [65, 0],
						"2": [65, 0],
						"3,4": [65, 0],
						"more": [64, 8]
					}
				}
			};

			if (birthdateYear >= 1936 && birthdateYear <= 1971) {
				let retirementAge;
				const yearKey = birthdateYear.toString();

				if (sex === "man") {
					retirementAge = retirementTable[yearKey]["man"];
				} else {
					let childrenKey;

					if (children < 3) {
						childrenKey = children.toString();
					} else if (children === 3 || children === 4) {
						childrenKey = "3,4";
					} else {
						childrenKey = "more";
					}

					retirementAge = retirementTable[yearKey]["woman"][childrenKey];
				}

				return {
					years: retirementAge[0],
					months: retirementAge[1]
				};
			}

			// For all born before 1936.
			let years;

			if (sex === "man") {
				years = 60;
			} else {
				switch (children) {
					case 0:         years = 57; break;
					case 1:         years = 56; break;
					case 2:         years = 55; break;
					case 3: case 4: years = 54; break;
					default:        years = 53; break;
				}
			}

			return {
				years: years,
				months: 0
			};
		}

		#formatRetirementAge(retirementAge) {
			let result = retirementAge.years + " letech";

			if (retirementAge.months === 1) {
				result += " a 1 měsíci";
			} else if (retirementAge.months > 1) {
				result += " a " + retirementAge.months + " měsících";
			}

			return result;
		}

		#formatCountdownDate(countdownDate) {
			const result = [];
			const years  = parseInt(countdownDate.format("Y"));
			const months = parseInt(countdownDate.format("M"));
			const days   = parseInt(countdownDate.format("D"));

			if (years === 1) {
				result.push("1 rok");
			} else if (years > 1 && years < 5) {
				result.push(years + " roky");
			} else if (years >= 5) {
				result.push(years + " let");
			}

			if (months === 1) {
				result.push("1 měsíc");
			} else if (months > 1 && months < 5) {
				result.push(months + " měsíce");
			} else if (months >= 5) {
				result.push(months + " měsíců");
			}

			if (days === 1) {
				result.push("1 den");
			} else if (days > 1 && days < 5) {
				result.push(days + " dny");
			} else if (days >= 5) {
				result.push(days + " dní");
			}

			let resultText = result.join(", ");

			// Replace last comma with "and".
			const lastCommaPos = resultText.lastIndexOf(",");

			if (lastCommaPos === -1) {
				return resultText;
			}

			const resultTextFirstPart = resultText.substring(0, lastCommaPos);
			const resultTextSecPart   = resultText.substring(lastCommaPos + 2);

			return resultTextFirstPart + " a " + resultTextSecPart;
		}

		#setResult(type, age, date, countdown) {
			const $template = (type === "after")
				? this.#$resultTemplateAfter
				: this.#$resultTemplateStandard;

			this.#$resultContent.innerHTML = $template.innerHTML;

			const $age       = this.#$result.querySelector(".dan-sentence-result.-age");
			const $date      = this.#$result.querySelector(".dan-sentence-result.-date");
			const $countdown = this.#$result.querySelector(".dan-sentence-result.-countdown");

			if ($age) {
				$age.textContent = age;
			}

			if ($date) {
				$date.textContent = date;
			}

			if ($countdown) {
				$countdown.textContent = countdown;
			}
		}

		#slideResultUp(callback = null) {
			this.#$result.style.height = this.#$result.scrollHeight + "px";

			setTimeout(() => {
				this.#$result.style.height = "0";
			}, 25);

			setTimeout(() => {
				this.#$result.classList.remove("-active");

				if (typeof callback === "function") {
					callback();
				}
			}, 175);
		}

		#slideResultDown(callback = null) {
			this.#$result.style.height = this.#$result.scrollHeight + "px";

			setTimeout(() => {
				this.#$result.style.height = "auto";
				this.#$result.classList.add("-active");

				if (typeof callback === "function") {
					callback();
				}
			}, 150);
		}

		#onBirthdateInput() {
			const inputs = this.getInputs();

			if (inputs.birthdate) {
				// noinspection JSUnresolvedReference
				this.#birthdateDatepicker.selectDate(inputs.birthdate, {
					silent: true
				});

				// noinspection JSUnresolvedReference
				this.#birthdateDatepicker.setViewDate(inputs.birthdate);
			}
		}

		#refreshChildrenInputVisibility() {
			const $childrenWrapper = this.#$widget.querySelector(".dan-children-input-wrapper");
			const childrenShown    = $childrenWrapper.classList.contains("-active");
			const inputs           = this.getInputs();

			if (inputs.sex === "woman" && !childrenShown) {
				$childrenWrapper.style.height = "0px";

				setTimeout(() => {
					$childrenWrapper.style.height = $childrenWrapper.scrollHeight + "px";
				}, 25);

				setTimeout(() => {
					$childrenWrapper.style.height = "auto";
					$childrenWrapper.classList.add("-active");
				}, 175);
			} else if (inputs.sex !== "woman" && childrenShown) {
				$childrenWrapper.style.height = $childrenWrapper.scrollHeight + "px";

				setTimeout(() => {
					$childrenWrapper.style.height = "0px";
				}, 25);

				setTimeout(() => {
					$childrenWrapper.classList.remove("-active");
				}, 175);
			}
		}

		getInputs() {
			// Sex.
			let sex = null;

			for (const $input of this.#$sexInputs) {
				if ($input.checked) {
					sex = $input.value;
					break;
				}
			}

			// Birthdate.
			const birthdateValue  = this.#$birthdateInput.value.trim();
			const birthdateParsed = /^(\d{2})\. (\d{2})\. (\d{4})$/.exec(birthdateValue);
			const birthdate       = (birthdateParsed)
				? new Date(birthdateParsed[3] + "-" + birthdateParsed[2] + "-" + birthdateParsed[1])
				: null;

			// Children.
			let children = parseInt(this.#$childrenInput.value);

			if (isNaN(children) || children < 0) {
				children = 0;
			}

			// Return.
			return {
				sex: sex,
				birthdate: birthdate,
				children: children
			};
		}
	}

	// Initialize widgets.
	const $widgets = document.querySelectorAll("form.dan-retirement-age-calculator");

	for (const $widget of $widgets) {
		$widget.calculator = new Retirement_Age_Calculator($widget);
		$widget.calculator.initialize();
	}
})();
